import React, { useEffect, useState } from "react";
import { Label } from "../../../api/label";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSAlert from "../../system-design/DSAlert";
import DSTextField from "../../system-design/DSTextField";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSErrorButton from "../../system-design/DSErrorButton";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";

interface CollectionLabelModalProps {
  show: boolean;
  label: Label | null;
  onClose: () => void;
  updateMutation: {
    mutateAsync: (args: { labelId: string; text: string }) => Promise<unknown>;
    isPending: boolean;
  };
  deleteMutation: {
    mutateAsync: (
      labelId: string,
      options?: {
        onSuccess?: () => void;
        onError?: (error: Error) => void;
      },
    ) => Promise<unknown>;
    isPending: boolean;
  };
}

const CollectionLabelModal: React.FC<CollectionLabelModalProps> = ({
  show,
  label,
  onClose,
  updateMutation,
  deleteMutation,
}) => {
  const [labelText, setLabelText] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { mutateAsync: updateLabel, isPending: isUpdating } = updateMutation;
  const { mutateAsync: deleteLabel, isPending: isDeleting } = deleteMutation;

  useEffect(() => {
    if (label) {
      setLabelText(label.text);
    }
    setShowSuccess(false);
    setErrorMessage("");
  }, [label, show]);

  if (!label) return null;

  const hasChanges = labelText !== label.text;

  const handleDelete = () => {
    if (!label.id) {
      return;
    }

    deleteLabel(label.id, {
      onSuccess: () => {
        onClose();
      },
      onError: (error) => {
        let message = "Failed to delete tag. Please try again.";

        const axiosError = error as {
          response?: {
            status?: number;
            data?: {
              message?: string;
              error?: string;
            };
          };
        };

        if (axiosError.response?.data?.message) {
          message = axiosError.response.data.message;
        }

        setErrorMessage(message);
      },
    });
  };

  const handleUpdate = async () => {
    if (!label.id || !hasChanges) {
      return;
    }

    setErrorMessage("");
    setShowSuccess(false);

    try {
      await updateLabel({
        labelId: label.id,
        text: labelText,
      });
      setShowSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error("Update error:", error);

      let message = "Failed to update tag. Please try again.";

      if (typeof error === "object" && error !== null && "response" in error) {
        const axiosError = error as {
          response?: {
            status?: number;
            data?: {
              message?: string;
              error?: string;
            };
          };
        };

        if (axiosError.response?.data?.message) {
          message = axiosError.response.data.message;
        }
      }

      setErrorMessage(message);
    }
  };

  return (
    <DSDialog open={show} onClose={onClose} maxWidth="md" fullWidth>
      <DSDialogTitle>
        Tag - <strong>{label.text}</strong>
      </DSDialogTitle>

      <DSDialogContent>
        {errorMessage && (
          <DSAlert severity="error" onClose={() => setErrorMessage("")}>
            {errorMessage}
          </DSAlert>
        )}

        {showSuccess && (
          <DSAlert severity="success">Tag updated successfully!</DSAlert>
        )}

        <DSBox sx={{ marginBottom: "20px", marginTop: "10px" }}>
          <DSTypography variant="h6">Edit Tag</DSTypography>
          <DSTextField
            fullWidth
            label="Tag Text"
            placeholder="Enter Tag Text"
            value={labelText}
            onChange={(e) => setLabelText(e.target.value)}
            sx={{ marginTop: "10px" }}
          />
        </DSBox>

        <DSBox sx={{ marginBottom: "20px" }}>
          <DSTypography variant="h6">Tag Information</DSTypography>
          <DSBox sx={{ display: "flex", gap: 4, marginTop: "10px" }}>
            <DSBox>
              <DSTypography variant="caption">Current Type</DSTypography>
              <DSTypography>{label.type}</DSTypography>
            </DSBox>
            <DSBox>
              <DSTypography variant="caption">Created At</DSTypography>
              <DSTypography>
                {new Date(label.createdAt).toLocaleString()}
              </DSTypography>
            </DSBox>
            <DSBox>
              <DSTypography variant="caption">Last Update</DSTypography>
              <DSTypography>
                {new Date(label.updatedAt).toLocaleString()}
              </DSTypography>
            </DSBox>
          </DSBox>
        </DSBox>
      </DSDialogContent>

      <DSDialogActions>
        <DSSecondaryButton onClick={onClose}>Close</DSSecondaryButton>
        <DSPrimaryButton
          onClick={handleUpdate}
          disabled={isUpdating || !hasChanges || !labelText.trim()}
        >
          {isUpdating ? "Updating..." : "Update Tag"}
        </DSPrimaryButton>

        <DSErrorButton
          onClick={handleDelete}
          disabled={isDeleting || isUpdating}
        >
          {isDeleting ? "Deleting..." : "Delete Tag"}
        </DSErrorButton>
      </DSDialogActions>
    </DSDialog>
  );
};

export default CollectionLabelModal;
